import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import { buildOpsExportMenu } from '@/tiktok-platform/utils/buildOpsExportMenu';
import {
  focusComplianceEntityField,
  mapTiktokComplianceEntityError,
  validateComplianceEntityForm,
} from '@/tiktok-platform/utils/mapTiktokComplianceEntityErrors';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function unwrapList(res, key) {
  const data = res?.data && typeof res.data === 'object' ? res.data : res;
  const list = Array.isArray(data?.[key])
    ? data[key]
    : (Array.isArray(data?.items) ? data.items : []);
  return {
    items: list,
    nextPageToken: String(data?.next_page_token || data?.nextPageToken || '').trim(),
    total: Number(data?.total_count ?? data?.total ?? list.length) || list.length,
  };
}

function regionalProfiles(row) {
  if (Array.isArray(row?.regional_profiles) && row.regional_profiles.length) {
    return row.regional_profiles;
  }
  return [row || {}];
}

function phoneText(phone) {
  if (!phone || typeof phone !== 'object') return '—';
  if (phone.availability === 'UNAVAILABLE') return i18n.t('compliance.euUnavailable', { ns: 'workbench' });
  return [phone.country_code, phone.local_number].filter(Boolean).join(' ') || '—';
}

function addressText(address) {
  if (address == null || address === '') return '—';
  if (typeof address === 'string') return address;
  return [address.street_address_line1, address.postal_code, address.country].filter(Boolean).join('，') || '—';
}

function explainComplianceError(message) {
  const text = String(message || '');
  if (/global seller|38023050|not supported/i.test(text)) {
    return i18n.t('compliance.euGlobalSeller', { ns: 'workbench' });
  }
  return text || i18n.t('compliance.euLoadFail', { ns: 'workbench' });
}

/** 表格复选框列：全选 / 单选 / 已选用 */
function useRowSelection(rows) {
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    setSelectedKeys([]);
  }, [rows]);
  const allKeys = useMemo(() => rows.map((row) => row.key), [rows]);
  const allSelected = allKeys.length > 0 && allKeys.every((key) => selectedKeys.includes(key));
  const toggleAll = () => setSelectedKeys(allSelected ? [] : allKeys);
  const toggleOne = (key) => setSelectedKeys((prev) => (
    prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
  ));
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedKeys.includes(row.key)),
    [rows, selectedKeys],
  );
  return {
    selectedKeys, allSelected, toggleAll, toggleOne, selectedRows,
  };
}

function FieldError({ message }) {
  if (!message) return null;
  return <span className="tko-field__error" role="alert">{message}</span>;
}

function ManufacturerCreateModal({ open, busy, onClose, onSubmit }) {
  const { t } = useTranslation('workbench');
  const bodyRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    registered_trade_name: '',
    email: '',
    country_code: '+353',
    local_number: '',
    address: '',
    locale: 'en-IE',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        registered_trade_name: '',
        email: '',
        country_code: '+353',
        local_number: '',
        address: '',
        locale: 'en-IE',
      });
      setFieldErrors({});
    }
  }, [open]);
  if (!open) return null;
  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setFieldErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };
  const applyErrors = (errors) => {
    setFieldErrors(errors);
    const first = Object.keys(errors)[0];
    if (first) {
      requestAnimationFrame(() => focusComplianceEntityField(bodyRef.current, first));
    }
  };
  const handleSubmit = async () => {
    const missing = validateComplianceEntityForm(form, 'manufacturer');
    if (Object.keys(missing).length) {
      applyErrors(missing);
      showToast(t('compliance.euNeedFields'), 'warning');
      return;
    }
    try {
      await onSubmit(form);
    } catch (e) {
      const mapped = mapTiktokComplianceEntityError(e, 'manufacturer');
      if (mapped.field) {
        applyErrors({ [mapped.field]: mapped.message });
      }
      showToast(mapped.message || e?.message || t('compliance.euCreateFail'), 'error');
    }
  };
  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true">
      <button type="button" className="tko-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div className="tko-batch-modal__panel">
        <header className="tko-batch-modal__head">
          <h2>{t('compliance.euCreateMfr')}</h2>
          <button type="button" className="tko-batch-modal__close" onClick={onClose}>×</button>
        </header>
        <div ref={bodyRef} className="tko-batch-modal__body tko-board-form">
          <label className={`tko-field${fieldErrors.name ? ' tko-field--invalid' : ''}`} data-field-error="name">
            <span className="tko-field__label">{t('compliance.euName')} <RequiredMark /></span>
            <input className="tko-input" value={form.name} disabled={busy} onChange={(e) => set('name', e.target.value)} />
            <FieldError message={fieldErrors.name} />
          </label>
          <label className={`tko-field${fieldErrors.registered_trade_name ? ' tko-field--invalid' : ''}`} data-field-error="registered_trade_name">
            <span className="tko-field__label">{t('compliance.euTrade')}</span>
            <input className="tko-input" value={form.registered_trade_name} disabled={busy} onChange={(e) => set('registered_trade_name', e.target.value)} />
            <FieldError message={fieldErrors.registered_trade_name} />
          </label>
          <label className={`tko-field${fieldErrors.email ? ' tko-field--invalid' : ''}`} data-field-error="email">
            <span className="tko-field__label">{t('compliance.euEmail')} <RequiredMark /></span>
            <input className="tko-input" value={form.email} disabled={busy} onChange={(e) => set('email', e.target.value)} />
            <FieldError message={fieldErrors.email} />
          </label>
          <div className="tko-board-form__row">
            <label className={`tko-field${fieldErrors.country_code ? ' tko-field--invalid' : ''}`} data-field-error="country_code">
              <span className="tko-field__label">{t('compliance.euCountryCode')} <RequiredMark /></span>
              <input className="tko-input" value={form.country_code} disabled={busy} onChange={(e) => set('country_code', e.target.value)} />
              <FieldError message={fieldErrors.country_code} />
            </label>
            <label className={`tko-field${fieldErrors.local_number ? ' tko-field--invalid' : ''}`} data-field-error="local_number">
              <span className="tko-field__label">{t('compliance.euLocalNo')} <RequiredMark /></span>
              <input className="tko-input" value={form.local_number} disabled={busy} onChange={(e) => set('local_number', e.target.value)} />
              <FieldError message={fieldErrors.local_number} />
            </label>
          </div>
          <label className={`tko-field${fieldErrors.address ? ' tko-field--invalid' : ''}`} data-field-error="address">
            <span className="tko-field__label">{t('compliance.euAddress')} <RequiredMark /></span>
            <input className="tko-input" value={form.address} disabled={busy} onChange={(e) => set('address', e.target.value)} />
            <FieldError message={fieldErrors.address} />
          </label>
          <label className={`tko-field${fieldErrors.locale ? ' tko-field--invalid' : ''}`} data-field-error="locale">
            <span className="tko-field__label">Locale</span>
            <input className="tko-input" value={form.locale} disabled={busy} onChange={(e) => set('locale', e.target.value)} />
            <FieldError message={fieldErrors.locale} />
          </label>
        </div>
        <footer className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--secondary" disabled={busy} onClick={onClose}>{t('shop.cancel')}</button>
          <button type="button" className="tko-btn tko-btn--primary" disabled={busy} onClick={() => { void handleSubmit(); }}>
            {busy ? t('compliance.euCreating') : t('compliance.euCreate')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ResponsibleCreateModal({ open, busy, onClose, onSubmit }) {
  const { t } = useTranslation('workbench');
  const bodyRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    country_code: '+353',
    local_number: '',
    street_address_line1: '',
    postal_code: '',
    country: 'IE',
    locale: 'en-IE',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        email: '',
        country_code: '+353',
        local_number: '',
        street_address_line1: '',
        postal_code: '',
        country: 'IE',
        locale: 'en-IE',
      });
      setFieldErrors({});
    }
  }, [open]);
  if (!open) return null;
  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setFieldErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };
  const applyErrors = (errors) => {
    setFieldErrors(errors);
    const first = Object.keys(errors)[0];
    if (first) {
      requestAnimationFrame(() => focusComplianceEntityField(bodyRef.current, first));
    }
  };
  const handleSubmit = async () => {
    const missing = validateComplianceEntityForm(form, 'responsiblePerson');
    if (Object.keys(missing).length) {
      applyErrors(missing);
      showToast(t('compliance.euNeedFields'), 'warning');
      return;
    }
    try {
      await onSubmit(form);
    } catch (e) {
      const mapped = mapTiktokComplianceEntityError(e, 'responsiblePerson');
      if (mapped.field) {
        applyErrors({ [mapped.field]: mapped.message });
      }
      showToast(mapped.message || e?.message || t('compliance.euCreateFail'), 'error');
    }
  };
  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true">
      <button type="button" className="tko-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div className="tko-batch-modal__panel">
        <header className="tko-batch-modal__head">
          <h2>{t('compliance.euCreateRp')}</h2>
          <button type="button" className="tko-batch-modal__close" onClick={onClose}>×</button>
        </header>
        <div ref={bodyRef} className="tko-batch-modal__body tko-board-form">
          <label className={`tko-field${fieldErrors.name ? ' tko-field--invalid' : ''}`} data-field-error="name">
            <span className="tko-field__label">{t('compliance.euPerson')} <RequiredMark /></span>
            <input className="tko-input" value={form.name} disabled={busy} onChange={(e) => set('name', e.target.value)} />
            <FieldError message={fieldErrors.name} />
          </label>
          <label className={`tko-field${fieldErrors.email ? ' tko-field--invalid' : ''}`} data-field-error="email">
            <span className="tko-field__label">{t('compliance.euEmail')} <RequiredMark /></span>
            <input className="tko-input" value={form.email} disabled={busy} onChange={(e) => set('email', e.target.value)} />
            <FieldError message={fieldErrors.email} />
          </label>
          <div className="tko-board-form__row">
            <label className={`tko-field${fieldErrors.country_code ? ' tko-field--invalid' : ''}`} data-field-error="country_code">
              <span className="tko-field__label">{t('compliance.euCountryCode')} <RequiredMark /></span>
              <input className="tko-input" value={form.country_code} disabled={busy} onChange={(e) => set('country_code', e.target.value)} />
              <FieldError message={fieldErrors.country_code} />
            </label>
            <label className={`tko-field${fieldErrors.local_number ? ' tko-field--invalid' : ''}`} data-field-error="local_number">
              <span className="tko-field__label">{t('compliance.euLocalNo')} <RequiredMark /></span>
              <input className="tko-input" value={form.local_number} disabled={busy} onChange={(e) => set('local_number', e.target.value)} />
              <FieldError message={fieldErrors.local_number} />
            </label>
          </div>
          <label className={`tko-field${fieldErrors.street_address_line1 ? ' tko-field--invalid' : ''}`} data-field-error="street_address_line1">
            <span className="tko-field__label">{t('compliance.euStreet')} <RequiredMark /></span>
            <input className="tko-input" value={form.street_address_line1} disabled={busy} onChange={(e) => set('street_address_line1', e.target.value)} />
            <FieldError message={fieldErrors.street_address_line1} />
          </label>
          <div className="tko-board-form__row">
            <label className={`tko-field${fieldErrors.postal_code ? ' tko-field--invalid' : ''}`} data-field-error="postal_code">
              <span className="tko-field__label">{t('compliance.euPostal')} <RequiredMark /></span>
              <input className="tko-input" value={form.postal_code} disabled={busy} onChange={(e) => set('postal_code', e.target.value)} />
              <FieldError message={fieldErrors.postal_code} />
            </label>
            <label className={`tko-field${fieldErrors.country ? ' tko-field--invalid' : ''}`} data-field-error="country">
              <span className="tko-field__label">{t('compliance.euCountry')} <RequiredMark /></span>
              <input className="tko-input" value={form.country} disabled={busy} onChange={(e) => set('country', e.target.value)} />
              <FieldError message={fieldErrors.country} />
            </label>
          </div>
          <label className={`tko-field${fieldErrors.locale ? ' tko-field--invalid' : ''}`} data-field-error="locale">
            <span className="tko-field__label">Locale</span>
            <input className="tko-input" value={form.locale} disabled={busy} onChange={(e) => set('locale', e.target.value)} />
            <FieldError message={fieldErrors.locale} />
          </label>
        </div>
        <footer className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--secondary" disabled={busy} onClick={onClose}>{t('shop.cancel')}</button>
          <button type="button" className="tko-btn tko-btn--primary" disabled={busy} onClick={() => { void handleSubmit(); }}>
            {busy ? t('compliance.euCreating') : t('compliance.euCreate')}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** 合规资料 · 制造商 */
export function ManufacturersPanel({ dimTabs }) {
  const { t } = useTranslation('workbench');
  const { concreteScopeId, shopDimensionTabs } = useTiktokPageShopScope();
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();
  const shopId = concreteScopeId;
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [neverSynced, setNeverSynced] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);

  const load = useCallback(async (nextPage = page, keywordOverride, pageSizeOverride) => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setNeverSynced(false);
      return;
    }
    const size = Math.max(1, Number(pageSizeOverride !== undefined ? pageSizeOverride : pageSize) || TIKTOK_OPS_DEFAULT_PAGE_SIZE);
    setLoading(true);
    setError('');
    try {
      const res = await tiktokPlatformAPI.searchManufacturers(shopId, {
        cached: true,
        page: nextPage,
        pageSize: size,
      });
      const payload = unwrapList(res, 'manufacturers');
      let list = payload.items;
      const q = String(keywordOverride !== undefined ? keywordOverride : keyword).trim().toLowerCase();
      if (q) {
        list = list.filter((row) => {
          const name = String(row.name || '').toLowerCase();
          const id = String(row.id || row.manufacturerId || '');
          return name.includes(q) || id.includes(q);
        });
      }
      setItems(list);
      setTotal(payload.total);
      setPage(nextPage);
      setLastSyncedAt(res?.lastSyncedAt || '');
      setNeverSynced(!!res?.neverSynced);
    } catch (e) {
      setItems([]);
      setTotal(0);
      const message = explainComplianceError(e?.message);
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, keyword, page, pageSize]);

  const onQuery = () => load(1);
  const onReset = () => {
    setKeyword('');
    load(1, '');
  };

  useEffect(() => { load(1); }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const track = await startJob({ kind: 'compliance', shopId, label: t('compliance.euSync') });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      await load(1);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      showToast(explainComplianceError(e?.message), 'error');
    }
  }, [shopId, startJob, onTrackDone, load, t]);

  const { refresh: onSync, refreshing: syncingLocal, cooldownSeconds } = useGuardedRefresh(handleSync, {
    minIntervalMs: 5000,
  });
  const syncing = syncingLocal || isBusy('compliance', shopId);

  const tableRows = useMemo(() => items.flatMap((row) => regionalProfiles(row).map((profile, index) => ({
    key: `${row.id || 'm'}-${profile.locale || index}`,
    id: row.id,
    profile,
  }))), [items]);
  const {
    selectedKeys, allSelected, toggleAll, toggleOne, selectedRows,
  } = useRowSelection(tableRows);
  const [exporting, setExporting] = useState(false);

  const toExportRows = (list) => list.map(({ id, profile }) => ({
    ID: id || '',
    [t('compliance.euLocale')]: profile.locale || '',
    [t('compliance.euName')]: profile.name || '',
    [t('compliance.euTrade')]: profile.registered_trade_name || '',
    [t('compliance.euEmail')]: profile.email || '',
    [t('compliance.euPhone')]: phoneText(profile.phone_number),
    [t('compliance.euAddress')]: addressText(profile.address),
  }));

  const exportRowsCsv = (list, name) => {
    if (!list.length) return;
    downloadCsv(name, ['ID', t('compliance.euLocale'), t('compliance.euName'), t('compliance.euTrade'), t('compliance.euEmail'), t('compliance.euPhone'), t('compliance.euAddress')], toExportRows(list));
  };

  const exportAll = async () => {
    if (!shopId || exporting) return;
    setExporting(true);
    try {
      const acc = [];
      let nextPage = 1;
      let guard = 0;
      while (guard < 50) {
        // eslint-disable-next-line no-await-in-loop
        const res = await tiktokPlatformAPI.searchManufacturers(shopId, {
          cached: true,
          page: nextPage,
          pageSize: 100,
        });
        const payload = unwrapList(res, 'manufacturers');
        acc.push(...payload.items);
        guard += 1;
        if (acc.length >= payload.total || payload.items.length < 100) break;
        nextPage += 1;
      }
      const q = keyword.trim().toLowerCase();
      const filtered = q
        ? acc.filter((row) => {
          const name = String(row.name || '').toLowerCase();
          const id = String(row.id || row.manufacturerId || '');
          return name.includes(q) || id.includes(q);
        })
        : acc;
      const flat = filtered.flatMap((row) => regionalProfiles(row).map((profile, index) => ({
        key: `${row.id || 'm'}-${profile.locale || index}`,
        id: row.id,
        profile,
      })));
      exportRowsCsv(flat, 'tiktok-合规资质-制造商.csv');
      showToast(t('compliance.euExportedMfr', { count: flat.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('compliance.euExportFail'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onCreate = async (form) => {
    setCreating(true);
    try {
      await tiktokPlatformAPI.createManufacturer(shopId, {
        name: form.name.trim(),
        registered_trade_name: form.registered_trade_name?.trim() || undefined,
        email: form.email.trim(),
        phone_number: {
          availability: 'AVAILABLE',
          country_code: form.country_code.trim(),
          local_number: form.local_number.trim(),
        },
        address: form.address.trim(),
        locale: form.locale?.trim() || undefined,
      });
      showToast(t('compliance.euMfrCreated'), 'success');
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            busy={loading || syncing || creating || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              pageCount: tableRows.length,
              selectedCount: selectedRows.length,
              onExportFiltered: exportAll,
              onExportPage: () => exportRowsCsv(tableRows, 'tiktok-合规资质-制造商-本页.csv'),
              onExportSelected: () => exportRowsCsv(selectedRows, 'tiktok-合规资质-制造商-选中.csv'),
            })}
            syncKinds={['compliance']}
            onSync={() => (shopId ? onSync() : showToast(t('returnList.pickShop'), 'warning'))}
            syncing={syncing}
            syncCooldownSeconds={cooldownSeconds}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedAt ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(lastSyncedAt) }) : (neverSynced ? t('tiktokCatalog.notSynced') : t('compliance.euNotLoaded'))}
            syncHint={t('compliance.euSyncMfr')}
            extraActions={[{ key: 'create', label: t('compliance.euCreateMfr'), disabled: !shopId || creating, onClick: () => setCreateOpen(true) }]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline">
              <span className="tko-field__label">{t('compliance.keyword')}</span>
              <input
                className="tko-input"
                value={keyword}
                disabled={!shopId}
                placeholder={t('compliance.euNameId')}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>
          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" disabled={!shopId || loading} onClick={onReset}>
              {t('shop.reset')}
            </button>
            <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" disabled={!shopId || loading} onClick={onQuery}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={(
          <div className="tko-dim-row">
            {dimTabs}
            {shopDimensionTabs}
          </div>
        )}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('compliance.euQueryMfr')}
      >
        <div className="tko-drafts__table-wrap tko-certs-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-certs-table">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-certs-th tko-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || !tableRows.length) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('compliance.selectAll')}
                    checked={allSelected}
                    disabled={!tableRows.length}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tko-certs-th">ID</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euLocale')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euName')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euTrade')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euEmail')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euPhone')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euAddress')}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || tableRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">
                        {!shopId
                          ? t('returnList.pickShop')
                          : (error || (neverSynced ? t('compliance.euEmptySync') : t('freightLib.noData')))}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : tableRows.map(({ key, id, profile }) => (
                <tr key={key} className={selectedKeys.includes(key) ? 'is-selected' : undefined}>
                  <td className="tko-certs-td tko-certs-td--check" onClick={() => toggleOne(key)}>
                    <input
                      type="checkbox"
                      aria-label={t('compliance.euSelectRow')}
                      checked={selectedKeys.includes(key)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(key)}
                    />
                  </td>
                  <td className="tko-certs-td">{id || '—'}</td>
                  <td className="tko-certs-td">{profile.locale || '—'}</td>
                  <td className="tko-certs-td">{profile.name || '—'}</td>
                  <td className="tko-certs-td">{profile.registered_trade_name || '—'}</td>
                  <td className="tko-certs-td">{profile.email || '—'}</td>
                  <td className="tko-certs-td">{phoneText(profile.phone_number)}</td>
                  <td className="tko-certs-td">{addressText(profile.address)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={(next) => load(next)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            load(1, undefined, size);
          }}
          ariaLabel={t('compliance.euMfrPager')}
        />
      </ProductListCard>
      <ManufacturerCreateModal open={createOpen} busy={creating} onClose={() => !creating && setCreateOpen(false)} onSubmit={onCreate} />
    </>
  );
}

/** 合规资料 · 负责人 */
export function ResponsiblePersonsPanel({ dimTabs }) {
  const { t } = useTranslation('workbench');
  const { concreteScopeId, shopDimensionTabs } = useTiktokPageShopScope();
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();
  const shopId = concreteScopeId;
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [neverSynced, setNeverSynced] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);

  const load = useCallback(async (nextPage = page, keywordOverride, pageSizeOverride) => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setNeverSynced(false);
      return;
    }
    const size = Math.max(1, Number(pageSizeOverride !== undefined ? pageSizeOverride : pageSize) || TIKTOK_OPS_DEFAULT_PAGE_SIZE);
    setLoading(true);
    setError('');
    try {
      const res = await tiktokPlatformAPI.searchResponsiblePersons(shopId, {
        cached: true,
        page: nextPage,
        pageSize: size,
      });
      const payload = unwrapList(res, 'responsible_persons');
      let list = payload.items;
      const q = String(keywordOverride !== undefined ? keywordOverride : keyword).trim().toLowerCase();
      if (q) {
        list = list.filter((row) => {
          const name = String(row.name || '').toLowerCase();
          const id = String(row.id || row.responsiblePersonId || '');
          return name.includes(q) || id.includes(q);
        });
      }
      setItems(list);
      setTotal(payload.total);
      setPage(nextPage);
      setLastSyncedAt(res?.lastSyncedAt || '');
      setNeverSynced(!!res?.neverSynced);
    } catch (e) {
      setItems([]);
      setTotal(0);
      const message = explainComplianceError(e?.message);
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, keyword, page, pageSize]);

  const onQuery = () => load(1);
  const onReset = () => {
    setKeyword('');
    load(1, '');
  };

  useEffect(() => { load(1); }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const track = await startJob({ kind: 'compliance', shopId, label: t('compliance.euSync') });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      await load(1);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      showToast(explainComplianceError(e?.message), 'error');
    }
  }, [shopId, startJob, onTrackDone, load, t]);

  const { refresh: onSync, refreshing: syncingLocal, cooldownSeconds } = useGuardedRefresh(handleSync, {
    minIntervalMs: 5000,
  });
  const syncing = syncingLocal || isBusy('compliance', shopId);

  const tableRows = useMemo(() => items.flatMap((row) => regionalProfiles(row).map((profile, index) => ({
    key: `${row.id || 'r'}-${profile.locale || index}`,
    id: row.id,
    profile,
  }))), [items]);
  const {
    selectedKeys, allSelected, toggleAll, toggleOne, selectedRows,
  } = useRowSelection(tableRows);
  const [exporting, setExporting] = useState(false);

  const toExportRows = (list) => list.map(({ id, profile }) => ({
    ID: id || '',
    [t('compliance.euLocale')]: profile.locale || '',
    [t('compliance.euPerson')]: profile.name || '',
    [t('compliance.euEmail')]: profile.email || '',
    [t('compliance.euPhone')]: phoneText(profile.phone_number),
    [t('compliance.euAddress')]: addressText(profile.address),
  }));

  const exportRowsCsv = (list, name) => {
    if (!list.length) return;
    downloadCsv(name, ['ID', t('compliance.euLocale'), t('compliance.euPerson'), t('compliance.euEmail'), t('compliance.euPhone'), t('compliance.euAddress')], toExportRows(list));
  };

  const exportAll = async () => {
    if (!shopId || exporting) return;
    setExporting(true);
    try {
      const acc = [];
      let nextPage = 1;
      let guard = 0;
      while (guard < 50) {
        // eslint-disable-next-line no-await-in-loop
        const res = await tiktokPlatformAPI.searchResponsiblePersons(shopId, {
          cached: true,
          page: nextPage,
          pageSize: 100,
        });
        const payload = unwrapList(res, 'responsible_persons');
        acc.push(...payload.items);
        guard += 1;
        if (acc.length >= payload.total || payload.items.length < 100) break;
        nextPage += 1;
      }
      const q = keyword.trim().toLowerCase();
      const filtered = q
        ? acc.filter((row) => {
          const name = String(row.name || '').toLowerCase();
          const id = String(row.id || row.responsiblePersonId || '');
          return name.includes(q) || id.includes(q);
        })
        : acc;
      const flat = filtered.flatMap((row) => regionalProfiles(row).map((profile, index) => ({
        key: `${row.id || 'r'}-${profile.locale || index}`,
        id: row.id,
        profile,
      })));
      exportRowsCsv(flat, 'tiktok-合规资质-负责人.csv');
      showToast(t('compliance.euExportedRp', { count: flat.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('compliance.euExportFail'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onCreate = async (form) => {
    setCreating(true);
    try {
      await tiktokPlatformAPI.createResponsiblePerson(shopId, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone_number: {
          country_code: form.country_code.trim(),
          local_number: form.local_number.trim(),
        },
        address: {
          street_address_line1: form.street_address_line1.trim(),
          postal_code: form.postal_code.trim(),
          country: form.country.trim().toUpperCase(),
        },
        locale: form.locale?.trim() || undefined,
      });
      showToast(t('compliance.euRpCreated'), 'success');
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            busy={loading || syncing || creating || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              pageCount: tableRows.length,
              selectedCount: selectedRows.length,
              onExportFiltered: exportAll,
              onExportPage: () => exportRowsCsv(tableRows, 'tiktok-合规资质-负责人-本页.csv'),
              onExportSelected: () => exportRowsCsv(selectedRows, 'tiktok-合规资质-负责人-选中.csv'),
            })}
            syncKinds={['compliance']}
            onSync={() => (shopId ? onSync() : showToast(t('returnList.pickShop'), 'warning'))}
            syncing={syncing}
            syncCooldownSeconds={cooldownSeconds}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedAt ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(lastSyncedAt) }) : (neverSynced ? t('tiktokCatalog.notSynced') : t('compliance.euNotLoaded'))}
            syncHint={t('compliance.euSyncRp')}
            extraActions={[{ key: 'create', label: t('compliance.euCreateRp'), disabled: !shopId || creating, onClick: () => setCreateOpen(true) }]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline">
              <span className="tko-field__label">{t('compliance.keyword')}</span>
              <input
                className="tko-input"
                value={keyword}
                disabled={!shopId}
                placeholder={t('compliance.euPersonId')}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>
          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" disabled={!shopId || loading} onClick={onReset}>
              {t('shop.reset')}
            </button>
            <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" disabled={!shopId || loading} onClick={onQuery}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={(
          <div className="tko-dim-row">
            {dimTabs}
            {shopDimensionTabs}
          </div>
        )}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('compliance.euQueryRp')}
      >
        <div className="tko-drafts__table-wrap tko-certs-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-certs-table">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-certs-th tko-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || !tableRows.length) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('compliance.selectAll')}
                    checked={allSelected}
                    disabled={!tableRows.length}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tko-certs-th">ID</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euLocale')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euPerson')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euEmail')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euPhone')}</th>
                <th scope="col" className="tko-certs-th">{t('compliance.euAddress')}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || tableRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">
                        {!shopId
                          ? t('returnList.pickShop')
                          : (error || (neverSynced ? t('compliance.euEmptySync') : t('freightLib.noData')))}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : tableRows.map(({ key, id, profile }) => (
                <tr key={key} className={selectedKeys.includes(key) ? 'is-selected' : undefined}>
                  <td className="tko-certs-td tko-certs-td--check" onClick={() => toggleOne(key)}>
                    <input
                      type="checkbox"
                      aria-label={t('compliance.euSelectRow')}
                      checked={selectedKeys.includes(key)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(key)}
                    />
                  </td>
                  <td className="tko-certs-td">{id || '—'}</td>
                  <td className="tko-certs-td">{profile.locale || '—'}</td>
                  <td className="tko-certs-td">{profile.name || '—'}</td>
                  <td className="tko-certs-td">{profile.email || '—'}</td>
                  <td className="tko-certs-td">{phoneText(profile.phone_number)}</td>
                  <td className="tko-certs-td">{addressText(profile.address)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={(next) => load(next)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            load(1, undefined, size);
          }}
          ariaLabel={t('compliance.euRpPager')}
        />
      </ProductListCard>

      <ResponsibleCreateModal open={createOpen} busy={creating} onClose={() => !creating && setCreateOpen(false)} onSubmit={onCreate} />
    </>
  );
}
